import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';

import { FootballBreadcrumbsComponent } from '@/app/components/shared/football-breadcrumbs/football-breadcrumbs.component';
import { NoResultComponent } from '@/app/components/shared/no-result/no-result.component';
import { SkeletonPreviewDetailsComponent } from '@/app/components/shared/skeleton';
import { SeoService } from '@/app/services/seo';
import { TranslationService } from '@/app/services/translation.service';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { CompetitionMatchSummary } from '../match-details/models';
import { DetailViewComponent, TeamTabsComponent } from './components';
import { ETeamTypeTap } from './models';
import { TeamDetailsService, TeamTabsService } from './services';
import {
  EquipmentComponent,
  MatchesComponent,
  ResultsComponent,
  StandingComponent,
} from './tabs';

@Component({
  selector: 'app-team-details',
  standalone: true,
  imports: [
    TeamTabsComponent,
    DetailViewComponent,
    MatchesComponent,
    ResultsComponent,
    StandingComponent,
    EquipmentComponent,
    NoResultComponent,
    SkeletonPreviewDetailsComponent,
    FootballBreadcrumbsComponent,
  ],
  templateUrl: './team-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FootballTeamDetailsComponent {
  private translationService = inject(TranslationService);
  private teamTabsService = inject(TeamTabsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private teamService = inject(TeamDetailsService);
  private seoService = inject(SeoService);

  fetchStatusComputed = computed(() => ({
    isLoading: this.teamService.isLoadingTeamDetails(),
    isError: this.teamService.isErrorTeamDetails(),
  }));

  teamInfo = computed(() => this.teamService.teamDetails());

  teamId = signal<string>('');
  slug = signal<string | null>(null);
  selectedHeaderTab = computed(() => this.teamTabsService.selectedTap());

  urlSVG = environment.urlIcon;
  urlIMG = environment.urlIMG;

  constructor() {
    window.scrollTo(0, 0);

    this.route.paramMap.subscribe((params) => {
      const id = params.get('teamId');
      const slug = params.get('slug');

      if (id) {
        this.teamId.set(id);
        this.slug.set(slug);
        this.teamService.getTeamDetails(id, () => {
          if (slug) {
            this.validateSlug();
          }

          // insert SEO
          const name = this.translationService.getTraduccionName(
            this.teamInfo()?.NameTranslation as any,
            this.teamInfo()?.Name || 'NA'
          );

          this.seoService.setTeamSeo({
            name,
            url: window.location.href,
            // image: '/assets/match.jpg',
          });
        });
        this.teamTabsService.selectedTap.set(ETeamTypeTap.MATCHES);
      }
    });
  }

  validateSlug() {
    const { Name, NameTranslation, Id } = this.teamInfo()!;

    // Redirigir a la URL correcta
    this.router.navigate(
      [
        this.translationService.getTraslateUrlTeamDetails(
          {
            es: NameTranslation?.['es'],
            en: NameTranslation?.['en'],
          },
          Name,
          Id
        ),
      ],
      {
        replaceUrl: true,
        queryParamsHandling: 'preserve',
      }
    );
  }

  // handleHeaderTap(tabValue: ETeamTypeTap) {
  //   this.selectedHeaderTab.set(tabValue);
  // }

  getTeamDetailInfo() {
    return {
      name: this.teamInfo()?.Name,
      logo: this.teamInfo()?.ImageUrl ?? this.urlIMG + 'default.png',
    };
  }

  getCompetitionBreadcrumbsData(): CompetitionMatchSummary {
    return {
      Id: this.teamInfo()?.Id || '',
      ImageUrl: this.teamInfo()?.ImageUrl || '',
      Name: this.teamInfo()?.Name || 'NA',
      NameTranslation: null,
      TsExternalId: this.teamInfo()?.TsExternalId || '',
      YoutubeUrl: '',
    };
  }

  ngOnDestroy() {
    this.teamId.set('');
    this.teamService.teamDetails.set(null);
  }
}
